import React, { useState, useEffect, useMemo } from 'react';
import { Check, XCircle, CheckCircle, X, Calendar, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DireccionAsignaturas() {
    const [horariosMatriculacion, setHorariosMatriculacion] = useState([]);
    const [horariosCPA, setHorariosCPA] = useState([]);
    const [horariosPAA, setHorariosPAA] = useState([]);
    const [gestiones, setGestiones] = useState([]);
    const [gestionesPendientes, setGestionesPendientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGestion, setSelectedGestion] = useState(new Date().getFullYear());
    const [selectedTab, setSelectedTab] = useState('matriculacion');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showCalendarModal, setShowCalendarModal] = useState(false);

    // Estado para rechazo con observación
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingItem, setRejectingItem] = useState(null);
    const [observacion, setObservacion] = useState('');
    const [observacionGestion, setObservacionGestion] = useState('');

    const [filtroDiaModal, setFiltroDiaModal] = useState('');
    const [filtroDocenteModal, setFiltroDocenteModal] = useState('');
    const [filtroSalaModal, setFiltroSalaModal] = useState('');

    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const diasCortos = { L: 'Lunes', M: 'Martes', MI: 'Miércoles', J: 'Jueves', V: 'Viernes' };

    const tabs = [
        { id: 'matriculacion', label: 'Matriculación' },
        { id: 'cpa', label: 'Curso de Preadmisión' },
        { id: 'paa', label: 'Prueba de Admisión Académica (PAA)' }
    ];

    // ============================================================
    // FIX: helpers para normalizar días y salas, porque cada
    // endpoint del backend guarda esta información de forma distinta:
    //  - Matriculación (asignaciones_horario):  columna "dias" (array Postgres, ej. "{Lunes,Miercoles}")
    //  - CPA / PAA (horarios_preadmision):      columna "dia_semana" (texto "Lunes,Martes")
    //                                            y opcionalmente "horarios_detalle" (jsonb con un
    //                                            día/hora/sala distinto por entrada)
    // El filtro original solo miraba "dia_semana", por eso en la
    // pestaña Matriculación (la que abre por defecto) el filtro de
    // Día no encontraba nunca coincidencias.
    // ============================================================
    const normalizarDias = (h) => {
        if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
            return h.horarios_detalle.map(d => (d.dia || '').trim().toLowerCase()).filter(Boolean);
        }
        if (h.dia_semana) {
            return h.dia_semana.split(',').map(d => d.trim().toLowerCase()).filter(Boolean);
        }
        if (Array.isArray(h.dias)) {
            return h.dias.map(d => String(d).trim().toLowerCase()).filter(Boolean);
        }
        if (typeof h.dias === 'string' && h.dias.startsWith('{')) {
            return h.dias.replace(/[{}]/g, '').split(',').map(d => d.trim().toLowerCase()).filter(Boolean);
        }
        return [];
    };

    // FIX: antes solo se miraba h.sala; ahora también se revisan
    // las salas por día dentro de horarios_detalle (CPA/PAA).
    const obtenerSalas = (h) => {
        if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
            return h.horarios_detalle.map(d => d.sala).filter(Boolean).map(String);
        }
        return [h.sala || h.aula].filter(Boolean).map(String);
    };

    useEffect(() => {
        loadGestionesPendientes();
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = 2020; y <= currentYear + 1; y++) {
            years.push(y);
        }
        setGestiones(years);
    }, []);

    useEffect(() => {
        if (selectedGestion) {
            loadAllData();
        }
    }, [selectedGestion]);

    const loadGestionesPendientes = async () => {
        try {
            const response = await fetch(`${API_URL}/direccion/gestiones-asignaciones-pendientes`);
            const data = await response.json();
            setGestionesPendientes(data.gestiones || []);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const sortAsignacionesCorrelativo = (arr) => {
        if (!Array.isArray(arr)) return [];
        return [...arr].sort((a, b) => {
            const nivelA = parseInt(a.nivel) || 0;
            const nivelB = parseInt(b.nivel) || 0;
            if (nivelA !== nivelB) return nivelA - nivelB;
            const codeA = a.codigo || a.asignatura || '';
            const codeB = b.codigo || b.asignatura || '';
            const numA = parseInt(codeA.replace(/\D/g, ''), 10) || 0;
            const numB = parseInt(codeB.replace(/\D/g, ''), 10) || 0;
            if (numA !== numB) return numA - numB;
            return codeA.localeCompare(codeB);
        });
    };

    const loadAllData = async () => {
        setLoading(true);
        try {
            const respMatriculacion = await fetch(`${API_URL}/direccion/asignaciones-matriculacion?gestion=${selectedGestion}`);
            const dataMatriculacion = await respMatriculacion.json();
            setHorariosMatriculacion(sortAsignacionesCorrelativo(dataMatriculacion.asignaciones || []));

            const respCPA = await fetch(`${API_URL}/jefatura/horarios-preadmision?gestion=${selectedGestion}&modalidad=CPA`);
            const dataCPA = await respCPA.json();
            setHorariosCPA(sortAsignacionesCorrelativo(dataCPA.horarios || []));

            const respPAA = await fetch(`${API_URL}/jefatura/horarios-preadmision?gestion=${selectedGestion}&modalidad=PAA`);
            const dataPAA = await respPAA.json();
            setHorariosPAA(sortAsignacionesCorrelativo(dataPAA.horarios || []));
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Aprobar individual
    const handleAprobarIndividual = async (horario, tipo) => {
        try {
            const endpoint = tipo === 'matriculacion'
                ? `${API_URL}/direccion/aprobar-asignacion/${horario.id_asignacion}`
                : `${API_URL}/direccion/aprobar-horario-preadmision/${horario.id_horario_preadmision}`;

            const response = await fetch(endpoint, { method: 'PUT' });
            if (response.ok) {
                loadAllData();
                loadGestionesPendientes();
            } else {
                const data = await response.json();
                alert(data.error || 'Error al aprobar');
            }
        } catch (error) {
            alert('Error al aprobar');
        }
    };

    const openRejectModal = (horario, tipo) => {
        setRejectingItem({ horario, tipo });
        setObservacion('');
        setShowRejectModal(true);
    };

    const handleRechazarIndividual = async () => {
        if (!observacion.trim()) {
            alert('La observación es obligatoria');
            return;
        }

        try {
            const { horario, tipo } = rejectingItem;
            const endpoint = tipo === 'matriculacion'
                ? `${API_URL}/direccion/rechazar-asignacion/${horario.id_asignacion}`
                : `${API_URL}/direccion/rechazar-horario-preadmision/${horario.id_horario_preadmision}`;

            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ observacion })
            });

            if (response.ok) {
                setShowRejectModal(false);
                loadAllData();
            } else {
                const data = await response.json();
                alert(data.error || 'Error al rechazar');
            }
        } catch (error) {
            alert('Error al rechazar');
        }
    };

    const handleAprobarGestion = async () => {
        try {
            const response = await fetch(`${API_URL}/direccion/aprobar-asignaciones-gestion`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gestion: selectedGestion })
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                loadGestionesPendientes();
                loadAllData();
            } else {
                alert(data.error || 'Error al aprobar');
            }
        } catch (error) {
            alert('Error al aprobar gestión');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleRechazarGestion = async () => {
        if (!observacionGestion.trim()) {
            alert('La observación es obligatoria para rechazar');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/direccion/rechazar-asignaciones-gestion`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gestion: selectedGestion, observacion: observacionGestion })
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                setObservacionGestion('');
                loadGestionesPendientes();
                loadAllData();
            } else {
                alert(data.error || 'Error al rechazar');
            }
        } catch (error) {
            alert('Error al rechazar gestión');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'aprobado': return 'bg-green-100 text-green-800';
            case 'rechazado': return 'bg-red-100 text-red-800';
            case 'pendiente': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getCurrentHorarios = () => {
        switch (selectedTab) {
            case 'matriculacion': return horariosMatriculacion;
            case 'cpa': return horariosCPA;
            case 'paa': return horariosPAA;
            default: return [];
        }
    };

    // Una sola fuente de verdad para "los horarios de la pestaña activa"
    const baseHorariosDelTab = useMemo(() => {
        if (selectedTab === 'cpa') return horariosCPA;
        if (selectedTab === 'paa') return horariosPAA;
        return horariosMatriculacion;
    }, [selectedTab, horariosMatriculacion, horariosCPA, horariosPAA]);

    const docentesDisponibles = useMemo(() => {
        const map = new Map();
        baseHorariosDelTab.forEach(h => {
            if (h.id_docente != null && !map.has(String(h.id_docente))) {
                const nombre = (h.docente_nombre || h.docente_apellido)
                    ? `${h.docente_nombre || ''} ${h.docente_apellido || ''}`.trim()
                    : `Docente ${h.id_docente}`;
                map.set(String(h.id_docente), { id_docente: h.id_docente, nombre });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [baseHorariosDelTab]);

    const salasDisponibles = useMemo(() => {
        const set = new Set();
        baseHorariosDelTab.forEach(h => obtenerSalas(h).forEach(s => set.add(s)));
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [baseHorariosDelTab]);

    // Filtro por Docente / Sala a nivel de MATERIA (si la materia tiene esa
    // sala/docente en algún día, pasa el filtro; el recorte fino por día
    // se hace después, en entradasPorDia).
    const horariosFiltradosModal = useMemo(() => {
        return baseHorariosDelTab.filter(h => {
            if (filtroDocenteModal && String(h.id_docente) !== String(filtroDocenteModal)) return false;
            if (filtroSalaModal) {
                const salasH = obtenerSalas(h);
                if (!salasH.includes(String(filtroSalaModal))) return false;
            }
            return true;
        });
    }, [baseHorariosDelTab, filtroDocenteModal, filtroSalaModal]);

    // ============================================================
    // FIX PRINCIPAL: aplanar cada materia en una entrada POR DÍA.
    // Antes se usaba h.hora_inicio / h.hora_fin / sala "generales" del
    // objeto materia para TODOS los días en los que figuraba, aunque
    // horarios_detalle tuviera hora/sala distintas por día. Eso hacía
    // que al filtrar por "Lunes" aparecieran materias cuyo Lunes real
    // tenía otra hora/sala, mostrando datos de un día equivocado.
    // Ahora cada entrada de "horarios" representa un solo día real.
    // ============================================================
    const entradasPorDia = useMemo(() => {
        const entradas = [];
        horariosFiltradosModal.forEach(h => {
            if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
                h.horarios_detalle.forEach(d => {
                    const dia = (d.dia || '').trim().toLowerCase();
                    if (!dia) return;
                    entradas.push({
                        ...h,
                        dia,
                        hora_inicio: d.hora_inicio || d.hora || h.hora_inicio,
                        hora_fin: d.hora_fin || h.hora_fin,
                        sala: d.sala || h.sala || h.aula,
                    });
                });
            } else {
                normalizarDias(h).forEach(dia => {
                    entradas.push({
                        ...h,
                        dia,
                        hora_inicio: h.hora_inicio,
                        hora_fin: h.hora_fin,
                        sala: h.sala || h.aula,
                    });
                });
            }
        });

        // Si además hay filtro de Sala, se vuelve a aplicar acá a nivel
        // de día, porque ahora cada entrada trae su sala real de ese día.
        const conSala = filtroSalaModal
            ? entradas.filter(e => String(e.sala) === String(filtroSalaModal))
            : entradas;

        // Filtro de Día: acá es donde antes fallaba, porque se filtraba
        // sobre la materia completa en vez de sobre el día puntual.
        return filtroDiaModal
            ? conSala.filter(e => e.dia === filtroDiaModal.toLowerCase())
            : conSala;
    }, [horariosFiltradosModal, filtroDiaModal, filtroSalaModal]);

    const isPendiente = gestionesPendientes.some(g => g.gestion === selectedGestion);
    const currentHorarios = getCurrentHorarios();

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-4 md:p-6 max-w-full mx-auto">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Dirección Académica &gt; Autorizaciones &gt; Asignaturas</h1>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSelectedTab(tab.id)}
                        className={`px-6 py-2.5 rounded-full font-medium transition ${selectedTab === tab.id
                            ? 'bg-blue-900 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <select
                            value={selectedGestion}
                            onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {gestiones.map(g => {
                                const esPendiente = gestionesPendientes.some(gp => gp.gestion === g);
                                return (
                                    <option key={g} value={g}>
                                        Gestión {g} {esPendiente ? '⚠️ Pendiente' : ''}
                                    </option>
                                );
                            })}
                        </select>
                        <span className="absolute right-3 top-[12px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    {isPendiente && (
                        <>
                            <button
                                onClick={() => { setConfirmAction('aprobar'); setShowConfirmModal(true); }}
                                className="px-5 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 flex items-center gap-2 font-medium"
                            >
                                <Check size={18} /> Aprobar Gestión
                            </button>
                            <button
                                onClick={() => { setConfirmAction('rechazar'); setObservacionGestion(''); setShowConfirmModal(true); }}
                                className="px-5 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 flex items-center gap-2 font-medium"
                            >
                                <XCircle size={18} /> Rechazar Gestión
                            </button>
                        </>
                    )}

                    {(selectedTab === 'cpa' || selectedTab === 'paa' || selectedTab === 'matriculacion') && (
                        <button
                            onClick={() => setShowCalendarModal(true)}
                            className="flex items-center gap-2 px-5 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium"
                        >
                            <Calendar size={18} />
                            Ver Horario
                        </button>
                    )}

                    {!isPendiente && (
                        <span className="px-4 py-2 bg-gray-100 text-gray-600 rounded-full text-sm">
                            Sin solicitudes pendientes para gestión {selectedGestion}
                        </span>
                    )}
                </div>
            </div>

            {/* Tabla de Horarios */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-3 text-center font-semibold border">No</th>
                                <th className="px-2 py-3 text-center font-semibold border">Código</th>
                                <th className="px-2 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-2 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-2 py-3 text-center font-semibold border">Día</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Inicio</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Fin</th>
                                <th className="px-2 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-2 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-2 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-2 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-2 py-3 text-center font-semibold border">Observación</th>
                                <th className="px-2 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentHorarios.length === 0 ? (
                                <tr><td colSpan="13" className="px-4 py-8 text-center text-gray-500 border">No hay horarios registrados</td></tr>
                            ) : (
                                currentHorarios.map((h, index) => (
                                    <tr key={h.id_asignacion || h.id_horario || h.id_horario_preadmision || index} className="hover:bg-gray-50">
                                        <td className="px-2 py-2 text-center border">{index + 1}</td>
                                        <td className="px-2 py-2 text-center border">{h.codigo || h.asignatura || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.asignatura_nombre || h.nombre || h.asignatura || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.docente_nombre ? `${h.docente_nombre} ${h.docente_apellido || ''}` : '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.dia_semana || h.dias || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.hora_inicio?.substring(0, 5) || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.hora_fin?.substring(0, 5) || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.capacidad || '30'}</td>
                                        <td className="px-2 py-2 text-center border">{h.sala || h.aula || '-'}</td>
                                        <td className="px-2 py-2 text-center border">{h.paralelo || '-'}</td>
                                        <td className="px-2 py-2 text-center border">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${getEstadoColor(h.estado_direccion)}`}>
                                                {h.estado_direccion || 'Borrador'}
                                            </span>
                                        </td>
                                        <td className="px-2 py-2 text-center border text-xs max-w-[150px] truncate" title={h.observaciones_direccion || ''}>
                                            {h.observaciones_direccion || '-'}
                                        </td>
                                        <td className="px-2 py-2 text-center border">
                                            {h.estado_direccion === 'pendiente' && (
                                                <div className="flex gap-1 justify-center">
                                                    <button
                                                        onClick={() => handleAprobarIndividual(h, selectedTab)}
                                                        className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                                                        title="Aprobar"
                                                    >
                                                        <CheckCircle size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectModal(h, selectedTab)}
                                                        className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                        title="Rechazar"
                                                    >
                                                        <XCircle size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Confirmación Gestión */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                {confirmAction === 'aprobar' ? 'Aprobar Gestión' : 'Rechazar Gestión'}
                            </h2>
                            <p className="text-gray-600 mb-4">
                                {confirmAction === 'aprobar'
                                    ? `¿Aprobar todas las asignaturas de gestión ${selectedGestion}?`
                                    : `¿Rechazar todas las asignaturas de gestión ${selectedGestion}?`
                                }
                            </p>

                            {confirmAction === 'rechazar' && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Observación (obligatoria) *
                                    </label>
                                    <textarea
                                        value={observacionGestion}
                                        onChange={(e) => setObservacionGestion(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                        rows="3"
                                        placeholder="Indique el motivo del rechazo..."
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmAction === 'aprobar' ? handleAprobarGestion : handleRechazarGestion}
                                    className={`px-4 py-2 text-white rounded-lg ${confirmAction === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                                >
                                    {confirmAction === 'aprobar' ? 'Aprobar' : 'Rechazar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Rechazo Individual */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="sticky top-0 bg-white border-b flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-blue-900">Rechazar Materia</h2>
                                <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={20} />
                                </button>
                            </div>

                            <p className="text-sm text-gray-600 mb-4">
                                Materia: <strong>{rejectingItem?.horario?.asignatura || rejectingItem?.horario?.nombre || 'N/A'}</strong>
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Observación (obligatoria) *
                                </label>
                                <textarea
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg text-sm"
                                    rows="3"
                                    placeholder="Indique el motivo del rechazo..."
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleRechazarIndividual}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Rechazar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Calendario/Horario Visual */}
            {showCalendarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 flex-wrap gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">
                                    {selectedTab === 'cpa' ? 'Curso Preadmisión' : selectedTab === 'paa' ? 'Prueba Admisión' : 'Matriculación'} - Gestión {selectedGestion}
                                </h2>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Filtro por Día */}
                                <select
                                    value={filtroDiaModal}
                                    onChange={(e) => setFiltroDiaModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Días</option>
                                    {diasSemana.map(dia => (
                                        <option key={dia} value={dia}>{dia}</option>
                                    ))}
                                    <option value="Sábado">Sábado</option>
                                </select>

                                {/* Filtro por Docente */}
                                <select
                                    value={filtroDocenteModal}
                                    onChange={(e) => setFiltroDocenteModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500 max-w-[200px]"
                                >
                                    <option value="">Todos los Docentes</option>
                                    {docentesDisponibles.map(doc => (
                                        <option key={doc.id_docente} value={doc.id_docente}>
                                            {doc.nombre}
                                        </option>
                                    ))}
                                </select>

                                {/* Filtro por Sala */}
                                <select
                                    value={filtroSalaModal}
                                    onChange={(e) => setFiltroSalaModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las Salas</option>
                                    {salasDisponibles.map(s => (
                                        <option key={s} value={s}>{`Sala ${s}`}</option>
                                    ))}
                                </select>

                                <button
                                    onClick={() => {
                                        const data = (entradasPorDia || []).map((h, idx) => ({
                                            'No': idx + 1,
                                            'Código': h.codigo || h.asignatura || '-',          // antes: h.asignatura || h.codigo
                                            'Asignatura': h.asignatura_nombre || h.nombre_asignatura || h.nombre_materia
                                                || (h.codigo ? h.asignatura : '') || '-',        // antes: solo asignatura_nombre/nombre_asignatura/nombre_materia
                                            'Sala': h.sala || '-',
                                            'Paralelo': h.paralelo || '-',
                                            'Docente': h.docente_nombre ? `${h.docente_nombre} ${h.docente_apellido || ''}` : '-',
                                            'Día': h.dia_semana || h.dia || '-',
                                            'Hora Inicio': h.hora_inicio,
                                            'Hora Fin': h.hora_fin,
                                            'Capacidad': h.capacidad || 30
                                        }));

                                        const ws = XLSX.utils.json_to_sheet(data);
                                        const wb = XLSX.utils.book_new();
                                        XLSX.utils.book_append_sheet(wb, ws, 'Horario');
                                        XLSX.writeFile(wb, `Horario_${selectedTab.toUpperCase()}_${selectedGestion}.xlsx`);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium transition shadow-sm"
                                    title="Exportar a Excel"
                                >
                                    <Download size={16} />
                                </button>
                                <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow overflow-auto p-4 bg-gray-100">
                            {(() => {
                                const rawHorarios = (entradasPorDia || []).filter(h => {
                                    const estado = (h.estado_direccion || '').toLowerCase();
                                    return estado !== 'pendiente' && estado !== 'rechazado';
                                });

                                // Normalización limpia para evitar desfases de horas y días múltiples
                                const horarios = rawHorarios;

                                const diasTabla = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes'];
                                const slotMinutes = 30;
                                const startHour = 6, endHour = 18;
                                const totalSlots = ((endHour - startHour) * 60) / slotMinutes;

                                const toMin = (hhmm) => {
                                    if (!hhmm) return 0;
                                    const partes = hhmm.substring(0, 5).split(':').map(Number);
                                    let h = partes[0] || 0;
                                    const m = partes[1] || 0;
                                    // Corrección automática por si viene en formato 12 horas o con desfase PM mal guardado (ej: 01:00 que en realidad es 13:00)
                                    if (h > 0 && h < 7 && h !== 12) {
                                        // Opcional: si detectas que tus clases de tarde dicen 01:00 en vez de 13:00, descomenta la siguiente línea:
                                        // h += 12; 
                                    }
                                    return h * 60 + m;
                                };

                                const dayData = diasTabla.map(dia => {
                                    const diaKey = dia.toLowerCase();
                                    const entries = horarios
                                        .filter(h => {
                                            const dStr = (h.dia || '').toLowerCase();
                                            return dStr.includes(diaKey) && h.hora_inicio && h.hora_fin;
                                        })
                                        .map(h => ({
                                            ...h,
                                            startMin: toMin(h.hora_inicio),
                                            endMin: toMin(h.hora_fin),
                                        }))
                                        .filter(h => h.startMin >= startHour * 60 && h.endMin <= endHour * 60) // Evita que se salgan del rango visual
                                        .sort((a, b) => a.startMin - b.startMin);

                                    const laneEnds = [];
                                    entries.forEach(e => {
                                        let laneIdx = laneEnds.findIndex(end => end <= e.startMin);
                                        if (laneIdx === -1) {
                                            laneIdx = laneEnds.length;
                                            laneEnds.push(e.endMin);
                                        } else {
                                            laneEnds[laneIdx] = e.endMin;
                                        }
                                        e.lane = laneIdx;
                                    });

                                    const maxLanes = Math.max(1, laneEnds.length);
                                    return { dia, diaKey, entries, maxLanes };
                                });

                                const totalLanes = dayData.reduce((sum, d) => sum + d.maxLanes, 0);
                                let acumulado = 0;
                                const dayOffsets = dayData.map(d => {
                                    const offset = acumulado;
                                    acumulado += d.maxLanes;
                                    return offset;
                                });

                                const paletaColores = [
                                    'bg-blue-600 hover:bg-blue-700',
                                    'bg-emerald-600 hover:bg-emerald-700',
                                    'bg-amber-600 hover:bg-amber-700',
                                    'bg-rose-600 hover:bg-rose-700',
                                    'bg-indigo-600 hover:bg-indigo-700',
                                    'bg-orange-600 hover:bg-orange-700',
                                    'bg-teal-600 hover:bg-teal-700',
                                    'bg-purple-600 hover:bg-purple-700',
                                    'bg-cyan-600 hover:bg-cyan-700',
                                    'bg-pink-600 hover:bg-pink-700'
                                ];

                                const obtenerColorDistintivo = (identificador, indice) => {
                                    let hash = 0;
                                    const str = String(identificador || indice);
                                    for (let i = 0; i < str.length; i++) {
                                        hash = str.charCodeAt(i) + ((hash << 5) - hash);
                                    }
                                    return paletaColores[Math.abs(hash) % paletaColores.length];
                                };

                                const subjectColorMap = {};
                                horarios.forEach((h, idx) => {
                                    const identifier = h.codigo || h.asignatura || `item-${idx}`;   // antes: h.asignatura || h.codigo
                                    if (!subjectColorMap[identifier]) {
                                        subjectColorMap[identifier] = obtenerColorDistintivo(identifier, idx);
                                    }
                                });

                                const rowHeight = 42;
                                const gridTemplateColumns = `110px repeat(${totalLanes}, minmax(140px, 1fr))`;

                                return (
                                    <div className="bg-white rounded-lg shadow overflow-auto" style={{ maxHeight: '75vh' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns, position: 'relative' }}>
                                            <div className="border bg-gray-100 font-semibold text-center py-2 sticky top-0 z-20"
                                                style={{ gridColumn: '1', gridRow: '1' }}>
                                                Intervalo
                                            </div>

                                            {dayData.map((d, di) => (
                                                <div key={d.dia}
                                                    className="border bg-gray-100 font-bold text-center py-2 sticky top-0 z-20 uppercase"
                                                    style={{ gridColumn: `${dayOffsets[di] + 2} / span ${d.maxLanes}`, gridRow: '1' }}>
                                                    {d.dia}
                                                </div>
                                            ))}

                                            {Array.from({ length: totalSlots }).map((_, i) => {
                                                const mins = startHour * 60 + i * slotMinutes;
                                                const h = Math.floor(mins / 60), m = mins % 60;
                                                const mins2 = mins + slotMinutes;
                                                const hEnd = Math.floor(mins2 / 60), mEnd = mins2 % 60;
                                                const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} - ${String(hEnd).padStart(2, '0')}:${String(mEnd).padStart(2, '0')}`;
                                                return (
                                                    <div key={i} className="border bg-gray-50 text-xs flex items-center justify-center px-1 font-mono text-gray-500"
                                                        style={{ gridColumn: '1', gridRow: i + 2, height: rowHeight }}>
                                                        {label}
                                                    </div>
                                                );
                                            })}

                                            {dayData.map((d, di) => (
                                                Array.from({ length: totalSlots }).map((_, i) => (
                                                    Array.from({ length: d.maxLanes }).map((_, li) => (
                                                        <div key={`bg-${di}-${i}-${li}`} className="border bg-white"
                                                            style={{ gridColumn: dayOffsets[di] + 2 + li, gridRow: i + 2, height: rowHeight }} />
                                                    ))
                                                ))
                                            ))}

                                            {dayData.map((d, di) => {
                                                return d.entries.map((e, ei) => {
                                                    const startSlotIndex = Math.floor((e.startMin - (startHour * 60)) / slotMinutes);
                                                    const durationMinutes = e.endMin - e.startMin;
                                                    const spanRows = Math.max(1, Math.ceil(durationMinutes / slotMinutes));
                                                    const gridRowStart = startSlotIndex + 2;
                                                    const gridCol = dayOffsets[di] + 2 + e.lane;

                                                    // Prioridad: codigo (matriculación) > asignatura (CPA/PAA, ahí "asignatura" ES el código)
                                                    const codigoMateria = e.codigo || e.asignatura || '???';
                                                    // El nombre completo puede venir en asignatura_nombre (CPA/PAA) o, en
                                                    // Matriculación, directamente en "asignatura" (ya que ahí NO es el código
                                                    // sino el nombre) — por eso el fallback condicional a e.asignatura.
                                                    const nombreMateria = e.asignatura_nombre || e.nombre_asignatura || e.nombre_materia
                                                        || (e.codigo ? e.asignatura : '') || '';

                                                    const identifier = codigoMateria || `item-${ei}`;
                                                    const boxColor = subjectColorMap[identifier] || paletaColores[0];
                                                    const docenteNombreCompleto = e.docente_nombre ? `${e.docente_nombre} ${e.docente_apellido || ''}` : '-';

                                                    return (
                                                        <div
                                                            key={`ev-${di}-${ei}`}
                                                            className={`p-2 text-white text-xs shadow-md flex flex-col justify-between overflow-hidden rounded transition-all ${boxColor}`}
                                                            style={{
                                                                gridColumn: gridCol,
                                                                gridRow: `${gridRowStart} / span ${spanRows}`,
                                                                margin: '1px',
                                                                zIndex: 10
                                                            }}
                                                            title={`${codigoMateria} ${nombreMateria ? `- ${nombreMateria}` : ''}\nDocente: ${docenteNombreCompleto}\nSala: ${e.sala || '-'}\nParalelo: ${e.paralelo || '-'}\nHorario: ${e.hora_inicio?.substring(0, 5)} - ${e.hora_fin?.substring(0, 5)}`}
                                                        >
                                                            <div>
                                                                <div className="font-bold truncate">
                                                                    {codigoMateria} {e.paralelo ? `(${e.paralelo})` : ''}
                                                                </div>
                                                                {nombreMateria && nombreMateria !== codigoMateria && (
                                                                    <div className="truncate opacity-90 text-[11px]">{nombreMateria}</div>
                                                                )}
                                                            </div>
                                                            <div className="flex justify-between text-[10px] opacity-85 mt-1 border-t border-white/20 pt-0.5">
                                                                <span>Sala: {e.sala || '?'}</span>
                                                                <span className="truncate max-w-[80px]">{e.docente_nombre?.split(' ')[0] || '-'}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}